/**
 * SC-209 phase 2: the shared screen or the whiteboard, in the big cell of the floating window.
 *
 * Two things live here because from the window's point of view they are one thing — "what everybody is
 * looking at" — and `pipLayout` gives them the same cell either way. What differs is how they arrive: a
 * screen share is a LiveKit track we already hold, and a whiteboard is a web page on another origin.
 *
 * **The whiteboard is a second iframe, not a moved one.** Moving the tab's iframe into this document would
 * reload the board, and a reload in the middle of someone drawing loses whatever they were drawing. So the
 * tab keeps its copy, untouched, and the window gets its own — same URL, same sandbox, same referrer policy.
 * Nothing here emits `dialog-opened`/`dialog-closed`: those tell the game a modal has the keyboard, and this
 * copy is in another window entirely.
 */

import React, { useCallback, useEffect, useState } from 'react'
import { VideoTrack } from '@livekit/components-react'

import type { PipContent } from './pipModel'
import {
  contentCellStyle,
  contentLabelStyle,
  contentOverlayStyle,
  contentRetryButtonStyle,
  contentVideoStyle,
  whiteboardFrameStyle,
  whiteboardFrameWrapperStyle,
} from './pipStyles'

/** The sandbox `WhiteboardModal` uses, copied exactly — a copy of a board is not a place to relax it. */
const WHITEBOARD_SANDBOX = 'allow-downloads allow-scripts allow-same-origin allow-forms'

/**
 * How long a board gets to load before the window offers to try again.
 *
 * There is no `onError` to wait for instead: React attaches a non-delegated `load` listener to an iframe and
 * **no `error` listener at all** (`setInitialProperties`, the `'embed' | 'object' | 'iframe'` case), so an
 * `onError` prop on an iframe is never called — the one on `WhiteboardModal`'s iframe has never fired either.
 * A clock is the only signal available. Ten seconds is the modal's own connection timeout.
 */
const WHITEBOARD_LOAD_TIMEOUT_MS = 10_000

export interface PipSharedContentCellProps {
  content: NonNullable<PipContent>
  /** The `grid-area` `pipLayout` set aside for it. */
  area: string
}

export function PipSharedContentCell({ content, area }: PipSharedContentCellProps) {
  if (content.kind === 'share') {
    return (
      <div style={contentCellStyle(area)} data-testid="pip-content" data-content-kind="share">
        <VideoTrack trackRef={content.trackRef} style={contentVideoStyle} />
        <span style={contentLabelStyle} data-testid="pip-content-label">
          {content.sharerName} is sharing
        </span>
      </div>
    )
  }

  return <PipWhiteboardCell content={content} area={area} />
}

function PipWhiteboardCell({
  content,
  area,
}: {
  content: Extract<NonNullable<PipContent>, { kind: 'whiteboard' }>
  area: string
}) {
  const [status, setStatus] = useState<'loading' | 'ready' | 'failed'>('loading')
  /** Bumped by "Try again"; it is the iframe's `key`, so a retry is a fresh element rather than a new URL. */
  const [attempt, setAttempt] = useState(0)

  // A different board in the same cell — or a retry of this one — starts its own load, and must not inherit
  // the last one's verdict.
  useEffect(() => {
    setStatus('loading')
    // Only condemns a load that is still pending: the timer outlives a board that has already arrived,
    // and a board that has arrived must not be declared broken ten seconds later.
    const timer = setTimeout(
      () => setStatus((current) => (current === 'loading' ? 'failed' : current)),
      WHITEBOARD_LOAD_TIMEOUT_MS
    )
    return () => clearTimeout(timer)
  }, [content.url, attempt])

  const retry = useCallback(() => setAttempt((n) => n + 1), [])

  return (
    <div style={contentCellStyle(area)} data-testid="pip-content" data-content-kind="whiteboard">
      {/* The wrapper is the blade: it clips, and the iframe inside it is what is offset. */}
      <div style={whiteboardFrameWrapperStyle}>
        <iframe
          key={`${content.whiteboardId}:${attempt}`}
          title={`whiteboard-${content.whiteboardId}`}
          src={content.url}
          sandbox={WHITEBOARD_SANDBOX}
          referrerPolicy="no-referrer"
          style={whiteboardFrameStyle}
          data-testid="pip-whiteboard-frame"
          onLoad={() => setStatus('ready')}
        />
      </div>

      {status !== 'ready' && (
        <div style={contentOverlayStyle} data-testid="pip-content-overlay">
          {status === 'loading' ? (
            <span>Loading whiteboard…</span>
          ) : (
            <>
              <span>The whiteboard did not load.</span>
              <button type="button" onClick={retry} style={contentRetryButtonStyle} data-testid="pip-content-retry">
                Try again
              </button>
            </>
          )}
        </div>
      )}

      <span style={contentLabelStyle} data-testid="pip-content-label">
        {content.participantName} is on the whiteboard
      </span>
    </div>
  )
}

export default PipSharedContentCell
